import { useState } from 'react';
import type { MemberPublic } from '@/lib/db/types';

export interface ComposeLiveBet {
  id: string;
  opponent_id?: string;
  type: string;
  terms: string;
  wager_cents: number;
  status: 'pending' | 'accepted' | 'won' | 'lost' | 'declined';
}

interface Props {
  bets: ComposeLiveBet[];
  setBets: (bets: ComposeLiveBet[]) => void;
  members: MemberPublic[];
}

function parseQuickBet(text: string, members: MemberPublic[]) {
  const m = text.match(/@(\w+)\s+(?:bet\s+)?\$?(\d+)\s+(.+)/i);
  if (!m) return null;
  const opp = members.find((x) => x.handle.toLowerCase() === m[1].toLowerCase());
  if (!opp) return { error: `Unknown member @${m[1]}` };
  return { opponentId: opp.id, opponentName: opp.name, amount: parseInt(m[2], 10), description: m[3].trim() };
}

export function LiveBetsPanel({ bets, setBets, members }: Props) {
  const [quickInput, setQuickInput] = useState('');
  const parsed = parseQuickBet(quickInput, members);
  const canSubmit = parsed && !('error' in parsed);

  const won = bets.filter((b) => b.status === 'won').reduce((a, b) => a + b.wager_cents / 100, 0);
  const lost = bets.filter((b) => b.status === 'lost').reduce((a, b) => a + b.wager_cents / 100, 0);
  const net = won - lost;

  const addBet = () => {
    if (!parsed || 'error' in parsed) return;
    const newBet: ComposeLiveBet = {
      id: `b${Date.now()}`,
      type: 'side',
      terms: parsed.description,
      wager_cents: parsed.amount * 100,
      status: 'pending',
      opponent_id: parsed.opponentId,
    };
    const next = [...bets, newBet];
    setBets(next);
    setQuickInput('');
    setTimeout(() => {
      setBets(next.map((b) => {
        if (b.id !== newBet.id || b.status !== 'pending') return b;
        return { ...b, status: Math.random() < 0.85 ? 'accepted' : 'declined' };
      }));
    }, 1600);
  };

  return (
    <div className="space-y-2">
      <div className="rounded border border-gold-deep bg-navy p-3 text-cream">
        <div className="mb-1.5 text-[9px] font-bold tracking-[0.15em] text-gold-bright">QUICK BET — TYPE IT</div>
        <input
          value={quickInput}
          onChange={(e) => setQuickInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) addBet(); }}
          placeholder="@bigd $25 closest to pin 17"
          className="w-full rounded-sm bg-cream-warm px-3 py-2.5 font-mono text-sm text-ink outline-none"
        />
        {parsed && 'error' in parsed && <div className="mt-1.5 text-[11px] text-bad">{parsed.error}</div>}
        {parsed && !('error' in parsed) && (
          <div className="mt-1.5 text-[11px] text-gold-bright">
            → ${parsed.amount} vs <strong>{parsed.opponentName.split(' ')[0]}</strong> · {parsed.description}
          </div>
        )}
        {canSubmit && (
          <button type="button" onClick={addBet} className="mt-2 w-full rounded bg-gold py-2 text-xs font-bold text-navy">
            Send bet
          </button>
        )}
      </div>

      {(won > 0 || lost > 0 || bets.length > 0) && (
        <div className="grid grid-cols-3 gap-2 rounded-2xl bg-paper p-3 shadow-bt text-center">
          <div>
            <div className="text-[10px] font-bold tracking-wide text-mute">WON</div>
            <div className="font-serif text-lg font-bold text-good">${won}</div>
          </div>
          <div className="border-x border-line">
            <div className="text-[10px] font-bold tracking-wide text-mute">NET</div>
            <div className={`font-serif text-lg font-bold ${net > 0 ? 'text-good' : net < 0 ? 'text-bad' : 'text-ink'}`}>
              {net >= 0 ? '+' : ''}${net}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-wide text-mute">ACTIVE</div>
            <div className="font-serif text-lg font-bold text-navy">{bets.filter((b) => b.status === 'accepted' || b.status === 'pending').length}</div>
          </div>
        </div>
      )}

      {bets.map((b) => {
        const opp = members.find((m) => m.id === b.opponent_id);
        return (
          <div key={b.id} className="flex items-center gap-3 rounded-xl border border-line bg-paper p-3">
            <div className="flex-1">
              <div className="text-sm font-bold text-navy">${b.wager_cents / 100} vs {opp?.name.split(' ')[0] || 'Member'}</div>
              <div className="text-xs text-ink-soft">{b.terms}</div>
            </div>
            <div className="flex gap-1">
              {b.status === 'accepted' && (
                <>
                  <button type="button" onClick={() => setBets(bets.map((x) => x.id === b.id ? { ...x, status: 'won' } : x))} className="rounded bg-good px-2 py-1 text-[10px] font-bold text-white">Won</button>
                  <button type="button" onClick={() => setBets(bets.map((x) => x.id === b.id ? { ...x, status: 'lost' } : x))} className="rounded bg-bad px-2 py-1 text-[10px] font-bold text-white">Lost</button>
                </>
              )}
              <span className={`rounded px-2 py-1 text-[10px] font-bold uppercase ${b.status === 'pending' ? 'bg-gold/20 text-gold-deep' : b.status === 'accepted' ? 'bg-green-100 text-good' : b.status === 'won' ? 'bg-green-500 text-white' : b.status === 'lost' ? 'bg-red-500 text-white' : 'bg-line text-mute'}`}>
                {b.status}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
